import { getAccountId } from '@seek/adv-account-selection';
import {
  type BusinessAddress,
  BusinessAddressForm,
  type BusinessAddressRef,
  type Country,
} from '@seek/hirer-business-address';
import type { FocusEvent, RefObject } from 'react';
import { Element } from 'react-scroll';

import { SECTION_BUSINESS_ADDRESS } from 'src/app/shared/constants/pageSectionName';
import { BUSINESS_ADDRESS_PRESSED } from 'src/app/shared/dataAnalytics/schema/constants';
import { useConfig } from 'src/app/shared/hooks/useConfig';
import { useAnalyticsTracking } from 'src/app/v2/hooks/useAnalyticsTracking';
import { useHirerAccount } from 'src/app/v2/hooks/useHirerAccount';
import {
  GetHirerAccountDocument,
  useUpdateBusinessAddressMutation,
} from 'src/graphql/generated';

interface BusinessAddressSectionProps {
  businessAddressRef?: RefObject<BusinessAddressRef>;
  onUpdateFailed: (error: Error) => void;
}

export const BusinessAddressSection: React.FC<BusinessAddressSectionProps> = ({
  businessAddressRef,
  onUpdateFailed,
}) => {
  const { getLanguage } = useConfig();
  const { trackingAdapter } = useAnalyticsTracking();
  const { postalAddress } = useHirerAccount() || {};
  const [updateBusinessAddress] = useUpdateBusinessAddressMutation({
    refetchQueries: [
      {
        query: GetHirerAccountDocument,
        variables: {
          // TO-DO: Remove this when we move to the new hirer account query
          adCentreAdvertiserId: getAccountId() || '',
        },
      },
    ],
  });

  const onBlur = (
    event: FocusEvent<HTMLInputElement | HTMLSelectElement>,
  ): void => {
    const { id } = event.target;

    const eventData = {
      eventName: BUSINESS_ADDRESS_PRESSED,
      objectInteraction: id.toLowerCase(),
    };

    trackingAdapter.sendLinkEvent(eventData);
  };

  const onSubmit = async (values: BusinessAddress) => {
    try {
      const { countryCode, useForBillingAddress, ...rest } = values;
      const { errors } = await updateBusinessAddress({
        variables: {
          input: {
            ...rest,
            useForBillingAddress: useForBillingAddress || true,
            countryCodeAlpha2: countryCode,
          },
        },
      });

      if (errors && errors.length > 0) {
        throw new Error(errors[0].message);
      }
    } catch (error) {
      if (error instanceof Error) {
        onUpdateFailed(error);
      }
      throw error;
    }
  };

  return (
    <Element name={SECTION_BUSINESS_ADDRESS}>
      <BusinessAddressForm
        businessAddressRef={businessAddressRef}
        language={getLanguage()}
        onSubmit={onSubmit}
        onBlur={onBlur}
        compactHeading={true}
        address={{
          countryCode: postalAddress?.country as Country,
          addressLines:
            postalAddress?.addressLines?.filter(
              (addressLine): addressLine is string => Boolean(addressLine),
            ) ?? [],
          suburb: postalAddress?.suburb ?? '',
          state: postalAddress?.state ?? '',
          postCode: postalAddress?.postCode ?? '',
        }}
      />
    </Element>
  );
};
